<template>
  <div
    class="overflow-hidden flex items-center justify-center"
    :class="component.wrapper"
    :data-testid="dataTestid"
  >
    <div
      v-if="$slots.header && props.layout === 'reviews'"
      class="px-4 text-center font-semibold text-2xl/[120%] md:text-4xl/[120%] text-semantic-surface-on-surface"
    >
      <slot name="header" :data="data" />
    </div>

    <NuxtLink
      ref="tp-ratings"
      class="px-4 flex gap-3 items-center justify-center cursor-pointer"
      :class="[
        component.ratingsWrapper,
        {
          'my-10': props.layout === 'reviews',
        },
        ratingsClass,
      ]"
      to="https://au.trustpilot.com/review/compareclub.com.au"
      target="_blank"
    >
      <div class="flex items-center gap-4 text-lg">
        <p
          v-if="!hideStarString"
          class="font-semibold"
          :class="component.rating"
        >
          {{ data?.starsString }}
        </p>

        <NuxtImg
          v-if="starType === 'image'"
          :src="`/images/truspilot/tp-stars-${data?.stars}.webp`"
          :alt="`trustpilot rate ${data?.stars}`"
          :class="component.stars.class"
          :width="component.stars.width"
          :height="component.stars.height"
          loading="lazy"
        />

        <V2AtomsBaseStars
          v-else
          :review="data?.stars"
          :size="size"
          :fill="fillColor"
        />
      </div>

      <div class="flex flex-wrap justify-center items-center gap-4">
        <p class="text-base/[150%] md:text-lg/[150%]">
          Based on
          <strong
            class="font-bold underline"
          >
            {{ data?.numberOfReviews?.total }} reviews
          </strong>

          on
        </p>

        <V2IconsTrustpilotLogo
          :inverted="starColor !== 'green'"
          :fill="inverted ? '#fff' : layout === 'reviews' ? '#000' : ''"
          :class="component.logo.class"
        />
      </div>
    </NuxtLink>

    <swiper-container
      v-if="props.layout === 'reviews' && !_isEmpty(reviews)"
      ref="swiper"
      slides-per-view="auto"
      init="false"
      :style="{
        '--swiper-pagination-bottom': '0',
        '--swiper-pagination-bullet-horizontal-gap': '8px',
      }"
    >
      <slot
        name="slider"
        :review-list="reviews.slice(0, 8)"
        :review-redirect-link="reviewRedirectLink"
      >
        <swiper-slide
          v-for="review in reviews.slice(0, 8)"
          :id="`review-${review.id}`"
          :key="review.id"
          class="exact-w-[296px] md:exact-w-[352px] min-h-[315px] md:min-h-[392px] relative !inline-flex flex-col items-start rounded-2xl md:rounded-3xl cursor-pointer bg-semantic-surface-default text-semantic-surface-on-surface"
        >
          <NuxtLink
            class="flex flex-col w-full min-h-[392px] rounded-2xl md:rounded-3xl shadow-elevation-1"
            :to="`https://au.trustpilot.com/reviews/${review.id}`"
            target="_blank"
          >
            <div
              class="flex flex-col gap-4 shadow-elevation-2 p-4 md:p-6 rounded-2xl md:rounded-3xl min-h-inherit w-full text-left"
            >
              <NuxtImg
                src="/images/truspilot/tp-quotes.webp"
                alt="tp quotes"
                width="40"
                height="40"
                class="wh-10"
                loading="lazy"
              />

              <p
                class="font-bold text-lg/[150%] truncate overflow-hidden w-full"
              >
                {{ review.title }}
              </p>

              <div class="flex gap-[13px]">
                <NuxtImg
                  :src="`/images/truspilot/tp-stars-${review.starValue}.webp`"
                  alt="tp star"
                  width="108"
                  height="18"
                  loading="lazy"
                />

                <time
                  class="text-semantic-surface-on-surface-variant text-xs/[150%]"
                  :datetime="toHours(review.createdAt)"
                >
                  {{ toHours(review.createdAt) }}
                </time>
              </div>

              <div class="flex-1">
                <div
                  class="tp-review min-h-[105px] md:min-h-[120px] pb-8 relative overflow-hidden"
                  :class="{
                    'h-[105px] md:h-[120px]': !review.collapsed,
                  }"
                >
                  <p>
                    {{ review.text }}
                  </p>

                  <div
                    v-show="!review.collapsed"
                    class="w-full h-8 absolute bottom-0 z-10 bg-gradient-to-b from-transparent to-white"
                  ></div>
                </div>

                <div
                  v-show="review.showReadmore && !review.collapsed"
                  class="w-full h-8 text-center flex items-center justify-center text-semantic-secondary-default font-semibold text-base/[100%] underline cursor-pointer"
                  @click.stop.prevent="() => (review.collapsed = !review.collapsed)"
                >
                  Read more
                </div>
              </div>

              <hr class="w-15 border-semantic-outline-variant mt-auto" />

              <p class="leading-[150%] font-bold">
                {{ review.customer }}
              </p>
            </div>
          </NuxtLink>
        </swiper-slide>
      </slot>
    </swiper-container>
  </div>
</template>

<script setup>
import _isEmpty from 'underscore/cjs/isEmpty.js'

import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

import { register } from 'swiper/element/bundle'
register()

// Extend the dayjs with relativeTime plugin
try {
  dayjs.extend(relativeTime)
} catch (error) {
  throw new Error(error?.message ?? String(error), { cause: error })
}

const trustpilotStore = useTrustpilotStore()

defineOptions({
  name: 'V2MoleculesReviews'
})

const props = defineProps({
  dataTestid: {
    type: String,
    default: ''
  },

  layout: {
    type: String,
    default: 'rating',
    validator: value => {
      const arr = ['rating', 'reviews']

      if (Array.isArray(value)) {
        return value.filter(e => arr.includes(e))
      }

      return arr.includes(value)
    }
  },

  inverted: {
    type: Boolean,
    default: false
  },

  direction: {
    type: String,
    default: 'horizontal',
    validator: value => ['vertical', 'horizontal'].includes(value)
  },

  size: {
    type: String,
    default: 'base',
    validator: value => ['base', 'large'].includes(value)
  },

  starColor: {
    type: String,
    default: 'green',
    validator: value => ['default', 'green'].includes(value)
  },

  stars: {
    type: String,
    default: '4,5'
  },

  ratingsClass: {
    type: String,
    default: ''
  },

  tags: {
    type: String,
    default: ''
  },

  reviewId: {
    type: String,
    default: '',
    required: false
  },

  // This is used to hide the star string in the rating layout
  hideStarString: {
    type: Boolean,
    default: false
  },

  // This is used to determine if the stars should be rendered as an image or a v2 component
  starType: {
    type: String,
    default: 'image',
    validator: value => ['image', 'component'].includes(value)
  },

  // This is used to set the fill color of the stars v2 component
  fillColor: {
    type: String,
    default: '#000'
  }
})

if (
  !trustpilotStore?.trustpilotData &&
  !trustpilotStore?.trustpilotDataHasError
) {
  await trustpilotStore.fetchTrustpilotData()
}

const swiper = ref()
const data = computed(() => trustpilotStore?.trustpilotData)
const reviews = ref(trustpilotStore.trustpilotReviews)

if (
  !trustpilotStore?.trustpilotReviews &&
  !trustpilotStore?.isLoadingTrustpilotReviews &&
  !trustpilotStore?.trustpilotReviewsHasError &&
  props.layout === 'reviews'
) {
  await trustpilotStore.fetchReviews(
    removeFalseyObj({
      stars: props.stars,
      tagValue: props.tags
    })
  )

  reviews.value = trustpilotStore.trustpilotReviews
}

onMounted(async () => {
  await nextTick()

  await wait(1000)

  if (props.layout === 'reviews') {
    const el = swiper.value

    if (el) {
      const params = {
        slidesPerView: 'auto', // Keep this as auto if you want the exact-w styles to work
        spaceBetween: 16,
        pagination: {
          clickable: true
        },
        breakpoints: {
          768: {
            spaceBetween: 32
          }
        },
        autoplay: {
          delay: 3000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true
        },
        injectStyles: [
          `
          :host .swiper-pagination {
            height: 8px;
          }

          :host .swiper-pagination-bullet-active {
            background-color: ${
              props.inverted
                ? semanticColorLookup('inverse.primary')
                : semanticColorLookup('primary.default')
            } !important;
          }

          :host .swiper-pagination-bullet {
            background-color: ${
              props.inverted
                ? semanticColorLookup('inverse.on-surface')
                : semanticColorLookup('outline.default')
            };
            width: 16px;
            height: 16px;
            position: relative;
            top: -9px;
          }

          :host .swiper {
            padding: 2px 16px 59px;
            width: calc(100% - 32px);
          }

          :host .swiper {
            max-width: 1280px;
            overflow: initial;
          }
        `
        ]
      }

      Object.assign(el, params)
      el.initialize()
    }
  }

  for (const key in reviews.value) {
    if (Object.prototype.hasOwnProperty.call(reviews.value, key)) {
      const review = reviews.value[key]

      if (review.collapsed === undefined) {
        review.collapsed = false
      }

      handleShowReadMore(review)
    }
  }
})

const component = computed(() => {
  const wrapper = (() => {
    const theme = getKey(props.layout, {
      rating: getKey(props.inverted, {
        false: 'text-semantic-surface-on-surface',
        true: 'bg-semantic-inverse-surface text-semantic-inverse-on-surface'
      }),
      reviews: getKey(props.inverted, {
        false: 'flex-col text-semantic-surface-on-surface',
        true: 'flex-col bg-semantic-inverse-surface text-semantic-inverse-on-surface'
      })
    })

    const text = getKey(props.size, {
      large: 'text-lg/[150%]',
      base: 'text-base/[150%]'
    })

    const height = getKey(props.layout, {
      rating: 'min-h-[4.625rem] md:min-h-[7.5rem]',
      reviews: ''
    })

    const padding = getKey(props.layout, {
      rating: 'py-6 md:py-10',
      reviews: 'py-10 md:py-20'
    })

    return `${theme} ${text} ${height} ${padding}`
  })()

  const ratingsWrapper = (() => {
    const direction = getKey(props.layout, {
      rating: getKey(props.direction, {
        vertical: 'flex-col',
        horizontal: 'flex-col md:flex-row'
      }),
      reviews: 'flex-col'
    })

    return `${direction}`
  })()

  const rating = getKey(props.layout, {
    rating: getKey(props.size, {
      large: 'text-2xl/[120%]',
      base: 'text-xl/[120%]'
    }),
    reviews: 'text-xl/[120%] md:text-2xl/[120%]'
  })

  const stars = (() => {
    const _class = getKey(props.size, {
      large: 'w-[6.75rem] md:w-[14.5rem] h-[1.15625rem] md:h-10',
      base: 'w-[6.75rem] h-[1.15625rem]'
    })

    const width = getKey(props.size, {
      large: 232,
      base: 108
    })

    const height = getKey(props.size, {
      large: 40,
      base: 18
    })

    return {
      class: _class,
      width,
      height
    }
  })()

  const logo = (() => {
    const _class = getKey(props.size, {
      large: 'w-[6.1475rem] md:w-[9.375rem] h-[1.625rem] md:h-10',
      base: 'w-[6.1475rem] h-[1.625rem]'
    })

    return {
      class: _class
    }
  })()

  return {
    wrapper,
    ratingsWrapper,
    rating,
    stars,
    logo
  }
})

function handleShowReadMore (review) {
  const reviewEl = document.getElementById(`review-${review.id}`)
  if (!reviewEl) {
    return
  }
  const text = reviewEl.querySelector('.tp-review p')
  // Ensure review.collapsed is initialized to false if it's not already
  if (review.collapsed === undefined) {
    review.collapsed = false
  }
  if(text === null) {
    review.showReadmore = false
    return
  }
  review.showReadmore = text.clientHeight > 120 && !review.collapsed
}

function toHours (date) {
  return dayjs(date).fromNow()
}

function reviewRedirectLink (review = null) {
  if (!review) {
    return
  }

  return window.open(
    `https://au.trustpilot.com/reviews/${review.id}`,
    '_blank'
  )
}
</script>
